import {
  paiseAmountToRupee,
  toIndianCurrencyString,
  bpsToPercentage,
  percentageToBps,
} from 'utils/Numbers';
import {
  ComplianceBehaviourKey,
  FlexibleBenefitFormData,
  TaxBehaviour,
  TaxCalculationBasis,
} from './Create/types';
import { CreateFlexibleBenefitInput } from 'api/gql-utils/flexibleBenefits/types';
import { FBP_BPS_PRECISION } from './constants';

// March — unclaimed declared amounts true up in the FY's final payroll month
// unless the admin picks an earlier month.
export const DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH = 3;

// Payroll months in FY order (April → March) for the unclaimed-taxable-from dropdown.
export const PAYROLL_MONTHS: Array<{ value: number; label: string }> = [
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
];

export const payrollMonthLabel = (month?: number): string =>
  PAYROLL_MONTHS.find((m) => m.value === (month || DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH))?.label ||
  'March';

type WageCalculationToggle = {
  label: string;
  behaviourKey: ComplianceBehaviourKey;
  field: string;
  isChecked: boolean;
};

export const getWageCalculationToggles = (
  formData: FlexibleBenefitFormData,
  includeBaseCompensationBehaviour = true,
): Array<WageCalculationToggle> => {
  const toggles: Array<WageCalculationToggle> = [
    {
      label: 'Part of PF wage',
      behaviourKey: 'pf_behaviour',
      field: 'include_in_pf_wages',
      isChecked: formData?.pf_behaviour?.include_in_pf_wages || false,
    },
    {
      label: 'Part of ESI wage',
      behaviourKey: 'esi_behaviour',
      field: 'include_in_esi_wages',
      isChecked: formData?.esi_behaviour?.include_in_esi_wages || false,
    },
    {
      label: 'Part of PT wage',
      behaviourKey: 'pt_behaviour',
      field: 'include_in_pt_wages',
      isChecked: formData?.pt_behaviour?.include_in_pt_wages || false,
    },
    {
      label: 'Part of LWF wage',
      behaviourKey: 'lwf_behaviour',
      field: 'include_in_lwf_wages',
      isChecked: formData?.lwf_behaviour?.include_in_lwf_wages || false,
    },
    {
      label: 'Part of Gratuity wages',
      behaviourKey: 'gratuity_behaviour',
      field: 'include_in_gratuity_wages',
      isChecked: formData?.gratuity_behaviour?.include_in_gratuity_wages || false,
    },
    {
      label: 'Part of Leave Encashment wages',
      behaviourKey: 'leave_encashment_behaviour',
      field: 'include_in_leave_encashment_wages',
      isChecked: formData?.leave_encashment_behaviour?.include_in_leave_encashment_wages || false,
    },
    {
      label: 'Part of OT wage',
      behaviourKey: 'ot_behaviour',
      field: 'include_in_ot_wages',
      isChecked: formData?.ot_behaviour?.include_in_ot_wages || false,
    },
  ];

  if (includeBaseCompensationBehaviour) {
    toggles.push({
      label: 'Part of Base Compensation',
      behaviourKey: 'base_compensation_behaviour',
      field: 'include_in_base_compensation',
      isChecked: formData?.base_compensation_behaviour?.include_in_base_compensation || false,
    });
  }

  return toggles;
};

const getPercentageValueForPrecision = (
  value: number,
  responsePrecision: number | undefined,
): number => (responsePrecision === FBP_BPS_PRECISION ? bpsToPercentage(value) : value);

export const formatBenefitAmount = (
  benefitSettings?: {
    type: string;
    value?: number;
    unit?: string;
    options?: number[];
    list_of_options_amount?: {
      amounts: number[];
      default_option_index: number;
      allow_multiple: boolean;
      amount_labels: string[];
    };
    fixed_amount?: {
      amount: number;
      description: string;
    };
    range_amount?: {
      max: number;
      min: number;
    };
    tiered_range_amount?: {
      tiers?: Array<{ id: string; label: string; max: number }>;
      default_tier_index?: number;
    };
  },
  responsePrecision?: number,
): { displayValue: string; description: string; lines?: string[] } => {
  if (!benefitSettings) {
    return { displayValue: '--', description: '' };
  }

  const { unit } = benefitSettings;
  const isPercentage = unit?.toLowerCase() === 'percentage_of_basic';
  const toDisplayValue = (value: number) =>
    isPercentage
      ? getPercentageValueForPrecision(value, responsePrecision)
      : paiseAmountToRupee(value);

  if (
    benefitSettings.list_of_options_amount &&
    benefitSettings.list_of_options_amount.amounts.length > 0
  ) {
    const formattedOptions = benefitSettings.list_of_options_amount.amounts
      .map((opt: number) => toIndianCurrencyString(paiseAmountToRupee(opt)))
      .join(', ');
    return {
      displayValue: isPercentage ? `${formattedOptions} of Basic` : formattedOptions,
      description: 'Multiple Values',
    };
  } else if (benefitSettings.fixed_amount) {
    const val = toDisplayValue(benefitSettings.fixed_amount.amount);
    const formattedValue = isPercentage
      ? `${val}% of Basic`
      : toIndianCurrencyString(val as number);
    return { displayValue: formattedValue, description: 'Max Limit' };
  } else if (
    benefitSettings.tiered_range_amount &&
    (benefitSettings.tiered_range_amount.tiers?.length ?? 0) > 0
  ) {
    const tierLines = (benefitSettings.tiered_range_amount.tiers ?? []).map(
      (tier) =>
        `${tier.label}: ${toIndianCurrencyString(paiseAmountToRupee(tier.max) as number)}`,
    );
    return {
      displayValue: tierLines.join('\n'),
      description: 'Tiers',
      lines: tierLines,
    };
  } else if (benefitSettings.range_amount) {
    const val = toDisplayValue(benefitSettings.range_amount.max);
    const formattedValue = isPercentage
      ? `${val}% of Basic`
      : toIndianCurrencyString(val as number);
    return { displayValue: formattedValue, description: 'Max Limit' };
  }

  return { displayValue: '--', description: '' };
};

/**
 * Converts API response format to form data format for editing
 * Transforms nested API structure to flattened form structure
 */
export const convertApiResponseToFormData = (apiData: any): FlexibleBenefitFormData | null => {
  const settings = apiData?.settings || {};

  if (!apiData) {
    return null;
  }

  const responsePrecision: number | undefined = apiData.response_precision;
  const isPercentage = settings.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';

  const rawMax =
    settings.benefit_amount_settings?.range_amount?.max ||
    settings.benefit_amount_settings?.fixed_amount?.amount ||
    0;

  return {
    id: apiData.id,
    name: apiData.name,
    isActive: apiData.is_active,
    componentType: apiData.component_type,
    displayName: settings.display_behaviour?.display_name || apiData.name,
    description: apiData.description || '',
    showInGrossSalary: settings.salary_settings?.show_in_gross_salary || false,
    nonCashBenefit: settings.salary_settings?.non_cash_benefit || false,

    declarationMethod: settings.declaration_settings?.declaration_enabled ? 'true' : 'false',

    declaration_settings: {
      declaration_enabled: settings.declaration_settings?.declaration_enabled ? 'true' : 'false',
      type: settings.declaration_settings?.type || 'FIXED',
    },

    benefit_amount_settings: {
      type: settings.benefit_amount_settings?.tiered_range_amount
        ? 'TIERED_RANGE'
        : settings.benefit_amount_settings?.list_of_options_amount
        ? 'LIST_OF_VALUES'
        : settings.benefit_amount_settings?.range_amount
        ? 'RANGE'
        : 'FIXED',
      unit: settings.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT',
      value: isPercentage
        ? getPercentageValueForPrecision(rawMax, responsePrecision)
        : paiseAmountToRupee(rawMax),
      options:
        settings?.benefit_amount_settings?.list_of_options_amount?.amounts.map((amount: number) =>
          paiseAmountToRupee(amount),
        ) || [],
      default_option_index:
        settings?.benefit_amount_settings?.list_of_options_amount?.default_option_index || 0,
      tiered_range_amount: settings.benefit_amount_settings?.tiered_range_amount
        ? {
            tiers: (settings.benefit_amount_settings.tiered_range_amount.tiers || []).map(
              (tier: { id: string; label: string; min?: number; max?: number }) => ({
                id: tier.id,
                label: tier.label,
                min: 0,
                max: paiseAmountToRupee(tier.max || 0) as number,
              }),
            ),
            default_tier_index:
              settings.benefit_amount_settings.tiered_range_amount.default_tier_index || 0,
          }
        : undefined,
    },

    tax_behaviour: {
      tax_exempt_in_old_regime: settings.tax_behaviour?.tax_exempt_in_old_regime || false,
      tax_exempt_in_new_regime: settings.tax_behaviour?.tax_exempt_in_new_regime || false,
      tax_exemption_section: settings.tax_behaviour?.tax_exemption_section || '',
      tax_deduction_behaviour: settings.tax_behaviour?.tax_deduction_behaviour || 'instant',
      tax_calculation_basis: {
        basis:
          (settings.tax_behaviour?.tax_calculation_basis?.basis as TaxCalculationBasis) ||
          'approved_amount_by_employer',
        unclaimed_taxable_from_month:
          settings.tax_behaviour?.tax_calculation_basis?.unclaimed_taxable_from_month ||
          DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH,
      },
    },

    arrear_behaviour: {
      consider_in_arrear: settings.arrear_behaviour?.consider_in_arrear || false,
      // Round-trip allow_negative as-is; there is no UI control for it
      ...(typeof settings.arrear_behaviour?.allow_negative === 'boolean'
        ? { allow_negative: settings.arrear_behaviour.allow_negative }
        : {}),
    },
    proration_behaviour: {
      support_proration: settings.proration_behaviour?.support_proration || false,
    },

    hold_settings: {
      hold_unclaimed_amount: settings.hold_settings?.hold_unclaimed_amount ? 'true' : 'false',
    },

    sa_adjustment_settings: {
      adjust_to_sa: settings.sa_adjustment_settings?.adjust_to_sa ?? true,
    },

    // Wage calculation (compliance) behaviours
    pf_behaviour: {
      include_in_pf_wages: settings.pf_behaviour?.include_in_pf_wages || false,
    },
    esi_behaviour: {
      include_in_esi_wages: settings.esi_behaviour?.include_in_esi_wages || false,
    },
    pt_behaviour: {
      include_in_pt_wages: settings.pt_behaviour?.include_in_pt_wages || false,
    },
    lwf_behaviour: {
      include_in_lwf_wages: settings.lwf_behaviour?.include_in_lwf_wages || false,
    },
    gratuity_behaviour: {
      include_in_gratuity_wages: settings.gratuity_behaviour?.include_in_gratuity_wages || false,
    },
    leave_encashment_behaviour: {
      include_in_leave_encashment_wages:
        settings.leave_encashment_behaviour?.include_in_leave_encashment_wages || false,
    },
    ot_behaviour: {
      include_in_ot_wages: settings.ot_behaviour?.include_in_ot_wages || false,
    },
    base_compensation_behaviour: {
      include_in_base_compensation:
        settings.base_compensation_behaviour?.include_in_base_compensation || false,
    },

    // Display Details
    showZero: settings.display_behaviour?.show_zero || false,
    visibleToEmployee: settings.display_behaviour?.visible_to_employee || false,
  };
};

/**
 * Converts flattened form data to the Flexible Benefits API payload format
 * Similar to convertFormDataToEarningSalaryComponentPayload in WizardViews
 */
export const convertFormDataToFlexiBenefitPayload = (
  formData: FlexibleBenefitFormData,
  id?: string,
  isFbpPercentagePrecisionEnabled = false,
  includeBaseCompensationBehaviour = true,
): CreateFlexibleBenefitInput => {
  const shouldForceHoldUnclaimedAmount = !Boolean(formData.showInGrossSalary);

  // Build tax behaviour object

  const isPercentageOfBasicValue = formData.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';
  const toPercentageValue = (value: number) =>
    isFbpPercentagePrecisionEnabled ? percentageToBps(value) : value;

  const taxBehaviour: TaxBehaviour = {
    tax_exempt_in_old_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_old_regime),
    tax_exempt_in_new_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_new_regime),
  };

  // Add tax exemption section if any regime is exempt
  if (
    formData.tax_behaviour?.tax_exempt_in_old_regime ||
    formData.tax_behaviour?.tax_exempt_in_new_regime
  ) {
    taxBehaviour.tax_exemption_section = formData.tax_behaviour?.tax_exemption_section || '';
  }

  // Add tax deduction behaviour
  taxBehaviour.tax_deduction_behaviour =
    formData.tax_behaviour?.tax_deduction_behaviour || 'instant';

  let benefitAmountSettings: CreateFlexibleBenefitInput['salary_component_requests'][number]['settings']['benefit_amount_settings'];

  if (formData.benefit_amount_settings?.type === 'LIST_OF_VALUES') {
    const options = formData.benefit_amount_settings.options || [];
    benefitAmountSettings = {
      unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
      type: formData.benefit_amount_settings.type,
      list_of_options_amount: {
        amounts: options.map((amount: number) => amount * 100),
        default_option_index: formData.benefit_amount_settings.default_option_index || 0,
        allow_multiple: false,
        amount_labels: [],
      },
    };
  } else if (formData.benefit_amount_settings?.type === 'TIERED_RANGE') {
    benefitAmountSettings = {
      unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
      type: formData.benefit_amount_settings.type,
      tiered_range_amount: {
        tiers: (formData.benefit_amount_settings.tiered_range_amount?.tiers || []).map(
          (tier) => ({
            id: tier.id,
            label: tier.label.trim(),
            min: 0,
            max: Number(tier.max || 0) * 100,
            step: 100,
            default_amount: 0,
          }),
        ),
        default_tier_index:
          formData.benefit_amount_settings.tiered_range_amount?.default_tier_index || 0,
      },
    };
  } else if (formData.benefit_amount_settings) {
    if (formData.benefit_amount_settings.type === 'RANGE') {
      benefitAmountSettings = {
        unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
        type: formData.benefit_amount_settings.type || 'RANGE',
        range_amount: {
          max:
            (isPercentageOfBasicValue
              ? toPercentageValue(Number(formData.benefit_amount_settings.value))
              : Number(formData.benefit_amount_settings.value) * 100) || 0,
          min: 0,
          step: isPercentageOfBasicValue ? toPercentageValue(1) : 1,
        },
      };
    } else {
      benefitAmountSettings = {
        unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
        type: formData.benefit_amount_settings.type || 'FIXED',
        fixed_amount: {
          amount:
            (isPercentageOfBasicValue
              ? toPercentageValue(Number(formData.benefit_amount_settings.value))
              : Number(formData.benefit_amount_settings.value) * 100) || 0,
          description: '',
        },
      };
    }
  }

  const settings = {
    salary_settings: {
      show_in_gross_salary: Boolean(formData.showInGrossSalary),
      non_cash_benefit: Boolean(formData.nonCashBenefit),
    },
    benefit_amount_settings: benefitAmountSettings,
    declaration_settings: {
      declaration_enabled: formData.declaration_settings?.declaration_enabled === 'true',
      type: formData.declaration_settings?.type || 'RANGE',
    },
    hold_settings: {
      hold_unclaimed_amount: shouldForceHoldUnclaimedAmount
        ? true
        : formData.hold_settings?.hold_unclaimed_amount === 'true',
    },
    // adjust_to_sa = false is only valid for gross components; force true otherwise.
    sa_adjustment_settings: {
      adjust_to_sa: formData.showInGrossSalary
        ? formData.sa_adjustment_settings?.adjust_to_sa ?? true
        : true,
    },
    tax_behaviour: {
      tax_exempt_in_old_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_old_regime),
      tax_exempt_in_new_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_new_regime),
      tax_calculation_basis: {
        basis: formData.tax_behaviour?.tax_calculation_basis?.basis || 'approved_amount_by_employer',
        unclaimed_taxable_from_month:
          formData.tax_behaviour?.tax_calculation_basis?.unclaimed_taxable_from_month ||
          DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH,
      },
      tax_exemption_section: formData.tax_behaviour?.tax_exemption_section || '',
      tax_deduction_behaviour: formData.tax_behaviour?.tax_deduction_behaviour || 'instant',
    },
    arrear_behaviour: {
      consider_in_arrear: Boolean(formData.arrear_behaviour?.consider_in_arrear),
      // No UI control exists for allow_negative, so any form value is initial-state
      // noise rather than user intent: arrears-enabled components always allow
      // negative recovery; otherwise round-trip whatever was stored.
      ...(formData.arrear_behaviour?.consider_in_arrear
        ? { allow_negative: true }
        : typeof formData.arrear_behaviour?.allow_negative === 'boolean'
          ? { allow_negative: formData.arrear_behaviour.allow_negative }
          : {}),
    },
    proration_behaviour: {
      support_proration: formData?.proration_behaviour?.support_proration || false,
    },
    display_behaviour: {
      show_zero: Boolean(formData.showZero),
      visible_to_employee: Boolean(formData.visibleToEmployee),
      display_name: formData.displayName!,
    },
    pf_behaviour: {
      include_in_pf_wages: Boolean(formData.pf_behaviour?.include_in_pf_wages),
    },
    esi_behaviour: {
      include_in_esi_wages: Boolean(formData.esi_behaviour?.include_in_esi_wages),
    },
    pt_behaviour: {
      include_in_pt_wages: Boolean(formData.pt_behaviour?.include_in_pt_wages),
    },
    lwf_behaviour: {
      include_in_lwf_wages: Boolean(formData.lwf_behaviour?.include_in_lwf_wages),
    },
    gratuity_behaviour: {
      include_in_gratuity_wages: Boolean(formData.gratuity_behaviour?.include_in_gratuity_wages),
    },
    leave_encashment_behaviour: {
      include_in_leave_encashment_wages: Boolean(
        formData.leave_encashment_behaviour?.include_in_leave_encashment_wages,
      ),
    },
    ot_behaviour: {
      include_in_ot_wages: Boolean(formData.ot_behaviour?.include_in_ot_wages),
    },
    ...(includeBaseCompensationBehaviour
      ? {
          base_compensation_behaviour: {
            include_in_base_compensation: Boolean(
              formData.base_compensation_behaviour?.include_in_base_compensation,
            ),
          },
        }
      : {}),
  };

  return {
    salary_component_requests: [
      {
        name: formData.name!,
        id,
        description: formData.description || '',
        category: 'FBP',
        is_active: Boolean(formData.isActive),
        component_type: formData.componentType || 'COMPONENT_TYPE_FBP',
        region: 'IN',
        request_precision:
          isFbpPercentagePrecisionEnabled && isPercentageOfBasicValue
            ? FBP_BPS_PRECISION
            : undefined,
        settings,
      },
    ],
  };
};
